import GeneralFormView from 'components/GeneralFormView';
import GeneralFormFields, { FormFieldActions } from 'components/GeneralFormView/types';
import { FormFieldsBuilder } from 'components/GeneralFormView/Utils/FormFieldsBuilder';
import {
  useObserver,
} from 'mobx-react';
import { RequestStatus, stores } from 'mobx-store';
import React, { useEffect, useState } from 'react';
import { PrizeDTO } from '../DtO/prizeDTO';
import { PrizeFormViewModel, PrizeToUpdate, PrizeType } from '../DtO/prizeFormViewModel';
import * as yup from 'yup';
import { ColorPlette } from '../types';
import { useParams } from 'react-router-dom';
import { Condition, ConditionalStatementTypes, ConditionExpression, ConditionStatement } from 'components/GeneralFormView/Utils/Condition';
import { getIn } from 'formik';
import { translate } from 'helperFunctions/translation';
import { FieldFile, isRemoteFile } from 'components/GeneralFormView/FormField/types';
import { uploadSingleFile } from 'helperFunctions/uploadService';

  const AddEditPrizeScreen: React.FC= () => {
    const {
    eventId,
    id,
  } = useParams<{eventId: string, id: string}>();

    const {
      wheelFortuneStore,
      wifiPackagesStore,
    } = stores;
    



    const getPrize = async (prizeId: string, language: string) => {
      await await wheelFortuneStore.getWheelPrize(Number(prizeId), language);
      return new PrizeFormViewModel(wheelFortuneStore.prize);
    };

    const updatePrize = async (
      prizeId: string,
      formValue: PrizeFormViewModel,
      language: string
    ) => {
      let imageUrl = null;
      if (formValue.textImage.length) {
        if (isRemoteFile(formValue.textImage[0])) {
          imageUrl = formValue.textImage[0].source;
        } else {
          imageUrl = await uploadSingleFile(formValue.textImage[0].file);
        }
      }
      const body: PrizeToUpdate = {
        isActive: formValue.isActive,
        type: formValue.type[0],
        textImage: imageUrl,
      };
      if (
        [PrizeType.WIFI_PACKAGE, PrizeType.physical, PrizeType.yango_promo_code].includes(formValue.type[0])
      ) {
        body.value = formValue.title;
      }
      await wheelFortuneStore.updatePrize(+prizeId, body, language);
    };

    const createPrize = async (formValue: PrizeFormViewModel) => {
      let imageUrl = null;
      if (formValue.textImage.length) {
        if (isRemoteFile(formValue.textImage[0])) {
          imageUrl = formValue.textImage[0].source;
        } else {
          imageUrl = await uploadSingleFile(formValue.textImage[0].file);
        }
      }
      const body: PrizeDTO = {
        ...formValue,
        type: formValue.type[0] || null,
        textImage: imageUrl,
        prizeWifi: {
          wifiPackageId: formValue.wifiPackageId
            ? formValue?.wifiPackageId[0]
            : 0,
        },
        value: [PrizeType.WIFI_PACKAGE, PrizeType.physical, PrizeType.yango_promo_code].includes(
          formValue.type[0]
        )
          ? formValue.title || ""
          : formValue.value || 0,
      };
      const { prizeId } = await wheelFortuneStore.createPrize(+eventId, body);
      return prizeId;
    };

    const hideOnEditCondition = (values: any, actions: Record<FormFieldActions, () => void>, location: string) => (
      new Condition({
        values,
        initialStatement: new ConditionStatement({
          action: actions[FormFieldActions.HIDE],
          type: ConditionalStatementTypes.IF,
          expression: new ConditionExpression({
            values,
            expression: (val) => {
              if (id) {
                return true;
              }
              return false;
            },
          }),
        }),
      })
      .addElse(new ConditionStatement({
        action: actions[FormFieldActions.ENABLE],
        type: ConditionalStatementTypes.ELSE,
        expression: new ConditionExpression({
          values,
          expression: (val) => true,
        }),
      })
      ));

    const colorItems = () => {
      return Object.keys(ColorPlette).map((key) =>{
        return {
          title: key,
          value: ColorPlette[key]
        }
      });
    }
    const { formFields } = new FormFieldsBuilder<PrizeFormViewModel>()
      .addField("type", {
        title: "type",
        type: GeneralFormFields.SELECT_FIELD,
        hasTranslations: false,
        initialValue: [PrizeType.points],
        fieldOptions: {
          isMultiple: false,
          items: [
            {
              title: translate(PrizeType.points, {}),
              value: PrizeType.points,
            },
            {
              title: translate(PrizeType.physical, {}),
              value: PrizeType.physical,
            },
            {
              title: translate(PrizeType.WIFI_PACKAGE, {}),
              value: PrizeType.WIFI_PACKAGE,
            },
            {
              title: translate(PrizeType.yango_promo_code, {}),
              value: PrizeType.yango_promo_code,
            },
          ],
        },
        condition: hideOnEditCondition,
      })
      .addField("wifiPackageId", {
        title: "Wifi Package",
        type: GeneralFormFields.SELECT_FIELD,
        hasTranslations: true,
        initialValue: wifiPackagesStore.wifiPackagesList.length
          ? [wifiPackagesStore.wifiPackagesList[0].packageId]
          : [],
        fieldOptions: {
          isMultiple: false,
          items: (wifiPackagesStore.wifiPackagesList || []).map((item) => ({
            title: item.name,
            value: item.packageId,
          })),
        },
        validationSchema: yup.array().of(yup.number().required()).optional(),
        condition: (values, actions, location) =>
          new Condition({
            values,
            initialStatement: new ConditionStatement({
              action: actions[FormFieldActions.HIDE],
              type: ConditionalStatementTypes.IF,
              expression: new ConditionExpression({
                values,
                expression: (val) => {
                  const value = getIn(
                    val,
                    location.split(".").slice(0, -1).join(".")
                  ).type;
                  if (value[0] === PrizeType.WIFI_PACKAGE && !id) {
                    return false;
                  }
                  return true;
                },
              }),
            }),
          }).addElse(
            new ConditionStatement({
              action: actions[FormFieldActions.ENABLE],
              type: ConditionalStatementTypes.ELSE,
              expression: new ConditionExpression({
                values,
                expression: (val) => false,
              }),
            })
          ),
      })
      .addField("value", {
        fieldOptions: {},
        hasTranslations: true,
        title: "Value",
        type: GeneralFormFields.INPUT_FIELD,
        validationSchema: yup.number().min(1),
        condition: (values, actions, location) =>
          new Condition({
            values,
            initialStatement: new ConditionStatement({
              action: actions[FormFieldActions.HIDE],
              type: ConditionalStatementTypes.IF,
              expression: new ConditionExpression({
                values,
                expression: (val) => {
                  const value = getIn(
                    val,
                    location.split(".").slice(0, -1).join(".")
                  ).type;
                  if (value[0] === PrizeType.points && !id) {
                    return false;
                  }
                  return true;
                },
              }),
            }),
          }).addElse(
            new ConditionStatement({
              action: actions[FormFieldActions.ENABLE],
              type: ConditionalStatementTypes.ELSE,
              expression: new ConditionExpression({
                values,
                expression: (val) => false,
              }),
            })
          ),
      })
      .addField("title", {
        fieldOptions: {},
        hasTranslations: true,
        title: "Title",
        type: GeneralFormFields.INPUT_FIELD,
        validationSchema: yup.string(),
        condition: (values, actions, location) =>
          new Condition({
            values,
            initialStatement: new ConditionStatement({
              action: actions[FormFieldActions.HIDE],
              type: ConditionalStatementTypes.IF,
              expression: new ConditionExpression({
                values,
                expression: (val) => {
                  const value = getIn(
                    val,
                    location.split(".").slice(0, -1).join(".")
                  ).type;
                  if (
                    [PrizeType.WIFI_PACKAGE, PrizeType.physical, PrizeType.yango_promo_code].includes(
                      value[0]
                    )
                  ) {
                    return false;
                  }
                  return true;
                },
              }),
            }),
          }).addElse(
            new ConditionStatement({
              action: actions[FormFieldActions.ENABLE],
              type: ConditionalStatementTypes.ELSE,
              expression: new ConditionExpression({
                values,
                expression: (val) => false,
              }),
            })
          ),
      })
      .addField("textImage", {
        hasTranslations: true,
        title: "Image",
        type: GeneralFormFields.FILE_PICKER,
        fieldOptions: {
          size: 200,
          accept: "image/*",
          multipleImages: false,
        },
        validationSchema: yup.array<FieldFile>().optional(),
        condition: (values, actions, location) =>
          new Condition({
            values,
            initialStatement: new ConditionStatement({
              action: actions[FormFieldActions.HIDE],
              type: ConditionalStatementTypes.IF,
              expression: new ConditionExpression({
                values,
                expression: (val) => {
                  const value = getIn(
                    val,
                    location.split(".").slice(0, -1).join(".")
                  ).type;
                  if (
                    [PrizeType.WIFI_PACKAGE, PrizeType.physical, PrizeType.yango_promo_code].includes(
                      value[0]
                    )
                  ) {
                    return false;
                  }
                  return true;
                },
              }),
            }),
          }).addElse(
            new ConditionStatement({
              action: actions[FormFieldActions.ENABLE],
              type: ConditionalStatementTypes.ELSE,
              expression: new ConditionExpression({
                values,
                expression: (val) => false,
              }),
            })
          ),
      })
      .addField("weight", {
        fieldOptions: {},
        hasTranslations: true,
        title: "Weight",
        type: GeneralFormFields.INPUT_FIELD,
        validationSchema: yup.number().min(1).required(),
        condition: hideOnEditCondition,
      })
      .addField("backgroundColor", {
        hasTranslations: true,
        title: "backgroundColor",
        type: GeneralFormFields.COLOR_FIELD,
        fieldOptions: {},
        condition: hideOnEditCondition,
      })
      .addField("maxWinners", {
        hasTranslations: false,
        title: "maxWinners",
        type: GeneralFormFields.INPUT_FIELD,
        fieldOptions: {},
        validationSchema: yup.number().min(-1).required(),
        condition: hideOnEditCondition,
      })
      .addField("isActive", {
        hasTranslations: false,
        title: "Active",
        type: GeneralFormFields.TOGGLE_FIELD,
        fieldOptions: {},
      });
      

      return useObserver(() => (
        <>
          <p><b>Note:</b> set maxWinners field -1 for unlimited prize</p>
          <hr/>
          <GeneralFormView<PrizeDTO, PrizeFormViewModel>
            defaultLang="ar"
            title="Prize"
            identifier="prizeId"
            otherLanguages={['en']}
            formData={formFields}
            create={createPrize}
            update={updatePrize}
            get={getPrize}
            isFetchSuccessful={wheelFortuneStore.status === RequestStatus.SUCCESS}
            createOrUpdateStatus={wheelFortuneStore.createAndUpdateStatus && wifiPackagesStore.loading ? RequestStatus.LOADING : RequestStatus.IDLE}
          />
        </>
      ));
    

  };
  
  export default AddEditPrizeScreen;
  